import { HttpErrorResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { ResultadoDescarga } from "src/app/shared/http/sello-de-error";
import { ParamReporteBean } from "src/app/shared/model/tickets/paramreporte.model";
import { TicketsService } from "../../tickets.service";

/**
 * Lo que comparten el diálogo de filtros y la vista previa del reporte de
 * tickets: los filtros ya resueltos, cómo viajan al servidor, el nombre del
 * archivo y la descarga misma. Vive aquí para que las dos pantallas
 * descarguen exactamente igual.
 */

/** Filtros ya resueltos: ids numéricos (0 = todos) y las etiquetas que imprime el Excel. */
export interface FiltrosReporteTickets {
  inicio: Date;
  fin: Date;
  idArea: number;
  idEstatus: number;
  idEmpresa: number;
  /** Vacío = "Todas"/"Todos": el servidor pone la palabra en el encabezado. */
  nombreArea: string;
  nombreEstatus: string;
  nombreEmpresa: string;
  /** false cuando el corporativo tiene una sola empresa y el campo ni se muestra. */
  conEmpresa: boolean;
}

export const MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** La vista previa recibe una COPIA: paginar allá no debe tocar lo capturado aquí. */
export function copiarFiltros(filtros: FiltrosReporteTickets): FiltrosReporteTickets {
  return {
    ...filtros,
    inicio: new Date(filtros.inicio),
    fin: new Date(filtros.fin),
  };
}

/** aaaa-mm-dd con las partes locales: toISOString() pasa a UTC y de noche cambiaba el día. */
export function fechaIso(fecha: Date): string {
  return [
    String(fecha.getFullYear()).padStart(4, "0"),
    String(fecha.getMonth() + 1).padStart(2, "0"),
    String(fecha.getDate()).padStart(2, "0"),
  ].join("-");
}

/** dd/mm/aaaa para leer; con "-" para el nombre del archivo, donde la diagonal no vale. */
export function fechaCorta(fecha: Date, separador = "/"): string {
  return [
    String(fecha.getDate()).padStart(2, "0"),
    String(fecha.getMonth() + 1).padStart(2, "0"),
    fecha.getFullYear(),
  ].join(separador);
}

export function nombreArchivoReporte(filtros: FiltrosReporteTickets): string {
  return `Reporte de tickets ${fechaCorta(filtros.inicio, "-")} a ${fechaCorta(filtros.fin, "-")}.xlsx`;
}

/** Los filtros como los espera el servidor (descarga y vista previa). */
export function aParametros(filtros: FiltrosReporteTickets): ParamReporteBean {
  const parametros = new ParamReporteBean();
  parametros.fechaInicio = fechaIso(filtros.inicio);
  parametros.fechaFin = fechaIso(filtros.fin);
  parametros.id_area = filtros.idArea;
  parametros.id_estatus = filtros.idEstatus;
  parametros.id_empresaSolicitante = filtros.idEmpresa;
  parametros.nombreArea = filtros.nombreArea;
  parametros.nombreEstatus = filtros.nombreEstatus;
  parametros.nombreEmpresa = filtros.nombreEmpresa;
  return parametros;
}

/** Una línea por filtro, para el aviso de "sin tickets" y los chips de la vista previa. */
export function resumenFiltros(filtros: FiltrosReporteTickets): string[] {
  const resumen = [
    `Área: ${filtros.nombreArea || "Todas"}`,
    `Estatus: ${filtros.nombreEstatus || "Todos"}`,
  ];
  if (filtros.conEmpresa) {
    resumen.push(`Empresa: ${filtros.nombreEmpresa || "Todas"}`);
  }
  resumen.push(`Del ${fechaCorta(filtros.inicio)} al ${fechaCorta(filtros.fin)}`);
  return resumen;
}

/**
 * Qué ampliar cuando no hay tickets: siempre el periodo y, solo si se
 * eligieron, el área, el estatus o la empresa (con "Todos" ya elegido no
 * tiene caso sugerirlo).
 */
export function sugerenciaSinTickets(filtros: FiltrosReporteTickets): string {
  const opciones: string[] = [];
  if (filtros.idArea) {
    opciones.push("Todas en Área");
  }
  if (filtros.idEstatus) {
    opciones.push("Todos en Estatus");
  }
  if (filtros.conEmpresa && filtros.idEmpresa) {
    opciones.push("Todas en Empresa");
  }
  if (!opciones.length) {
    return "Amplía el periodo.";
  }
  const ultima = opciones.pop();
  const eleccion = opciones.length ? `${opciones.join(", ")} y ${ultima}` : ultima;
  return `Amplía el periodo o elige ${eleccion}.`;
}

/**
 * Pide el Excel y, si llega, lo guarda con el nombre del periodo. Devuelve
 * el resultado para que la pantalla diga qué pasó: archivo o "sin tickets";
 * un error de negocio llega como error de rxjs con el motivo del servidor.
 */
export function descargarReporteTickets(
  servicio: TicketsService,
  filtros: FiltrosReporteTickets
): Observable<ResultadoDescarga> {
  return servicio.descargarReporte(aParametros(filtros)).pipe(
    tap((resultado) => {
      if (resultado.tipo === "archivo") {
        guardarArchivo(resultado.blob, nombreArchivoReporte(filtros));
      }
    })
  );
}

/** Ofrece el blob como descarga con un enlace temporal; no hace falta tenerlo en la plantilla. */
export function guardarArchivo(blob: Blob, nombre: string): void {
  const archivo = blob.type === MIME_XLSX ? blob : new Blob([blob], { type: MIME_XLSX });
  const url = URL.createObjectURL(archivo);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombre;
  enlace.style.display = "none";
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Revocar en el mismo tick cancela la descarga en algunos navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * El motivo que se le puede mostrar a la persona. Los Error vienen del
 * servidor (MESSAGE o pMensaje); un HttpErrorResponse es la red o un 500,
 * cuyo texto técnico no le sirve a nadie.
 */
export function motivoDelError(error: unknown, porOmision: string): string {
  if (error instanceof HttpErrorResponse) {
    return porOmision;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return porOmision;
}
